import { INFO_NOT_AVAILABLE_NA } from '@backoffice-frontend/common'
import { FccPassengerCheckInStatus } from '@backoffice-frontend/graphql/subscription'

import { MoiaTag } from '@backoffice-frontend/patterns'
import { Restricted } from '@backoffice-frontend/restricted'
import { BoumRight } from '@moia-dev/moia-token-claims'
import {
  AccessibilityIcon,
  CheckCircleSolidIcon,
  ChildseatIcon,
  ClockDashedIcon,
  ClockIcon,
  ForbiddenIcon,
  MinusCircleSolidIcon,
  WheelchairIcon,
  XCircleSolidIcon,
} from '@moia-dev/pace-icons'
import { Badge, Text } from '@moia-dev/pace-web'
import { isPast } from 'date-fns'
import { useTranslation } from 'react-i18next'
import type {
  AdPassengerRowCustomerFragment,
  AdPassengerRowSeatInfoFragment,
} from './AdPassengerRow.hook'

type AdPassengerRowProps = {
  customer: AdPassengerRowCustomerFragment | null
  seats: AdPassengerRowSeatInfoFragment
  authenticationState?: FccPassengerCheckInStatus
  eta?: Date
  isNoShow: boolean
}

export const AdPassengerRow = ({
  customer,
  seats,
  authenticationState,
  eta,
  isNoShow,
}: AdPassengerRowProps) => {
  const { t } = useTranslation()

  const isAwaitingCheckIn =
    authenticationState === FccPassengerCheckInStatus.InProgress &&
    eta &&
    !isPast(eta)
  const isCheckInOverdue =
    authenticationState === FccPassengerCheckInStatus.InProgress &&
    eta &&
    isPast(eta)
  const isCheckInSuccessful =
    authenticationState === FccPassengerCheckInStatus.Confirmed
  const isManualNoShowRequested =
    authenticationState === FccPassengerCheckInStatus.ManualNoShowRequested &&
    eta &&
    isPast(eta)
  const isManualNoShowPending =
    authenticationState === FccPassengerCheckInStatus.ManualNoShowPending
  const isManualNoShowTriggered =
    authenticationState === FccPassengerCheckInStatus.NoShowTriggered
  const isTripCancelled =
    authenticationState === FccPassengerCheckInStatus.Cancelled

  return (
    <div
      css={theme => ({
        display: 'flex',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
      })}
    >
      <div
        css={theme => ({
          display: 'flex',
          alignItems: 'center',
          gap: `${theme.spacing(0.5)} ${theme.spacing(1)}`,
          flexWrap: 'wrap',
        })}
      >
        <Restricted
          requiredRights={[
            BoumRight.BACKOFFICE_CUSTOMER_R,
            BoumRight.BACKOFFICE_CUSTOMER_NAME_R,
          ]}
          replacement={<ForbiddenIcon />}
        >
          <Text css={theme => ({ color: theme.semantic.ColorContentReduced })}>
            {customer?.firstName && customer.lastName
              ? `${customer.firstName} ${customer.lastName}`
              : INFO_NOT_AVAILABLE_NA}
          </Text>
        </Restricted>
        <MoiaTag
          css={theme => ({
            minWidth: theme.spacing(2),
            justifyContent: 'center',
          })}
          color="info"
        >
          {seats.seats}
        </MoiaTag>
        <div
          css={theme => ({
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            flexWrap: 'wrap',
          })}
        >
          {customer?.extendedWalkingTime && (
            <MoiaTag color="warning" icon={<AccessibilityIcon />}>
              {t('adFleetMap_badge_passengerListMobilityImpaired_caption')}
            </MoiaTag>
          )}
          {customer?.visualImpairment && (
            <MoiaTag color="warning" icon={<AccessibilityIcon />}>
              {t('adFleetMap_badge_passengerListVisuallyImpaired_caption')}
            </MoiaTag>
          )}
        </div>
      </div>
      <div css={theme => ({ display: 'flex', gap: theme.spacing(1) })}>
        {seats.wheelchairs > 0 && (
          <MoiaTag
            color="info"
            icon={<WheelchairIcon aria-label="Wheelchair" />}
          />
        )}
        {seats.childSeats + seats.boosterSeats > 0 && (
          <MoiaTag
            color="info"
            icon={<ChildseatIcon aria-label="Child seat" />}
          />
        )}
        {isAwaitingCheckIn && (
          <Badge
            variant="neutral"
            label={t('adFleetMap_badge_passengerList_pending_label')}
            icon={<ClockDashedIcon />}
          />
        )}
        {isCheckInOverdue && (
          <Badge
            label={t('adFleetMap_badge_passengerList_overdue_label')}
            variant={'warning'}
          />
        )}
        {isCheckInSuccessful && (
          <Badge
            label={t('adFleetMap_badge_passengerList_checkedIn_label')}
            variant={'success'}
            icon={<CheckCircleSolidIcon />}
          />
        )}
        {(isManualNoShowRequested || isManualNoShowPending) && (
          <Badge
            label={t('adFleetMap_badge_passengerList_overdue_label')}
            variant={'warning'}
            icon={<ClockIcon />}
          />
        )}
        {(isManualNoShowTriggered || isNoShow) && (
          <Badge
            label={t('adFleetMap_badge_passengerList_noShow_label')}
            variant={'critical'}
            icon={<XCircleSolidIcon />}
          />
        )}
        {isTripCancelled && !isNoShow && (
          <Badge
            label={t('common_cancelled_label')}
            variant={'critical'}
            icon={<MinusCircleSolidIcon />}
          />
        )}
      </div>
    </div>
  )
}
